/**
 * useWonderWorld.js
 *
 * Extracted from App.js — owns all WonderWorld game state and logic.
 * App.js previously held ~26 useState calls and ~15 functions for WW inline.
 * This hook exposes a stable interface; App.js only needs to spread the return.
 *
 * Data dependencies (imported from separate modules):
 *   worldOrder, worlds, questionsByWorld, bossQuestions — src/data/wonderWorldData.js
 *   loadProgress, loadHistory, saveProgress, saveHistory, clearHistory
 *                                               — src/wonderWorldProgress.js
 *
 * Props:
 *   lang        — current language ("en" | "fr" | "zh")
 *   page        — current page string
 *   setPage     — App-level page setter
 *   t           — i18n key lookup (for ui copy keys)
 *   pickInline  — (en, fr) => string  (inline bilingual text)
 *   localizeText — (text) => string   (lang-aware translation)
 */

import { useState, useEffect } from "react";
import { worldOrder, worlds, questionsByWorld, bossQuestions } from "./data/wonderWorldData";
import {
  loadProgress,
  loadHistory,
  saveProgress,
  saveHistory,
  clearHistory,
  STORAGE_KEY_FEEDBACK
} from "./wonderWorldProgress";

const SAVE_DEBOUNCE_MS = 180;
const MAX_HISTORY_ENTRIES = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function readJsonFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

function getQuestionKey(question) {
  return question ? `${question.type || "unknown"}::${question.q || question.question || ""}` : "";
}

function getQuestionChoiceKey(worldId, nodeId, choiceText) {
  return `${worldId}::${nodeId}::${choiceText}`;
}

function getPostQuestionNode(worldId, nodeId) {
  const currentNode = worlds[worldId]?.[nodeId];
  if (!currentNode) return null;
  const nextChoice = currentNode.choices?.find(
    (c) => c.next !== "question" && c.next !== "boss"
  );
  return nextChoice?.next || null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useWonderWorld({ lang, page, setPage, t, pickInline, localizeText }) {
  const saved = loadProgress();
  const savedHistory = loadHistory();

  const [world, setWorld] = useState(saved.world);
  const [node, setNode] = useState(saved.node);
  const [completed, setCompleted] = useState(saved.completed);
  const [unlockedWorlds, setUnlockedWorlds] = useState(saved.unlockedWorlds);
  const [question, setQuestion] = useState(saved.question);
  const [message, setMessage] = useState(null);
  const [stars, setStars] = useState(saved.stars);
  const [score, setScore] = useState(saved.score);
  const [difficulty, setDifficulty] = useState(saved.difficulty);
  const [decisionLog, setDecisionLog] = useState(saved.decisionLog);
  const [questionCount, setQuestionCount] = useState(saved.questionCount);
  const [correctCount, setCorrectCount] = useState(saved.correctCount);
  const [sessionStart] = useState(saved.sessionStart);
  const [history, setHistory] = useState(savedHistory);
  const [lastQuestionType, setLastQuestionType] = useState(saved.lastQuestionType);
  const [askedQuestionKeys, setAskedQuestionKeys] = useState(saved.askedQuestionKeys);
  const [feedbackChoice, setFeedbackChoice] = useState("");
  const [feedbackNote, setFeedbackNote] = useState("");
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState(null);
  const [lastResultKind, setLastResultKind] = useState(saved.lastResultKind);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showFinalEndingAfterSummary, setShowFinalEndingAfterSummary] = useState(
    saved.showFinalEndingAfterSummary
  );
  const [levelState, setLevelState] = useState(saved.levelState);
  const [pendingBossQueue, setPendingBossQueue] = useState(saved.pendingBossQueue);
  const [usedQuestionChoiceKeys, setUsedQuestionChoiceKeys] = useState(
    saved.usedQuestionChoiceKeys
  );
  const [pendingStoryNode, setPendingStoryNode] = useState(saved.pendingStoryNode);

  // ── Persistence (debounced) ───────────────────────────────────────────────
  useEffect(() => {
    const timer = window.setTimeout(() => {
      saveProgress({
        lang, page, world, node, completed, unlockedWorlds, stars, score,
        difficulty, decisionLog, questionCount, correctCount, sessionStart,
        lastQuestionType, askedQuestionKeys, levelState, pendingBossQueue,
        usedQuestionChoiceKeys, pendingStoryNode, question, lastResultKind,
        showFinalEndingAfterSummary
      });
      saveHistory(history);
    }, SAVE_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [
    lang, page, world, node, completed, unlockedWorlds, stars, score,
    difficulty, decisionLog, questionCount, correctCount, sessionStart,
    history, lastQuestionType, askedQuestionKeys, levelState, pendingBossQueue,
    usedQuestionChoiceKeys, pendingStoryNode, question, lastResultKind,
    showFinalEndingAfterSummary
  ]);

  // ── Game logic ────────────────────────────────────────────────────────────

  function unlockNextWorld(currentWorld) {
    const idx = worldOrder.indexOf(currentWorld);
    if (idx < 0 || idx >= worldOrder.length - 1) return;
    const nextWorld = worldOrder[idx + 1];
    setUnlockedWorlds((prev) => prev.includes(nextWorld) ? prev : [...prev, nextWorld]);
  }

  function getQuestionForWorld(currentWorld, currentDifficulty) {
    const worldPools = questionsByWorld[currentWorld];
    if (!worldPools) return null;

    const currentPool = (worldPools[currentDifficulty] || []).filter((q) => q.type !== "math");
    const allPool = ["easy", "medium", "hard"]
      .flatMap((level) => worldPools[level] || [])
      .filter((q) => q.type !== "math");
    const seenKeys = new Set(askedQuestionKeys);

    const pickRandom = (pool) => {
      if (!pool || pool.length === 0) return null;
      return pool[Math.floor(Math.random() * pool.length)];
    };

    const nextQuestion =
      pickRandom(currentPool.filter((q) => !seenKeys.has(getQuestionKey(q)) && q.type !== lastQuestionType)) ||
      pickRandom(currentPool.filter((q) => !seenKeys.has(getQuestionKey(q)))) ||
      pickRandom(allPool.filter((q) => !seenKeys.has(getQuestionKey(q)) && q.type !== lastQuestionType)) ||
      pickRandom(allPool.filter((q) => !seenKeys.has(getQuestionKey(q))));

    if (!nextQuestion) return null;

    setLastQuestionType(nextQuestion.type);
    setAskedQuestionKeys((prev) => {
      const key = getQuestionKey(nextQuestion);
      return prev.includes(key) ? prev : [...prev, key];
    });

    return nextQuestion;
  }

  function adjustDifficulty(nextScore) {
    if (nextScore >= 3) setDifficulty("hard");
    else if (nextScore >= 1) setDifficulty("medium");
    else setDifficulty("easy");
  }

  function choose(choice) {
    if (choice.tag) {
      setDecisionLog((prev) => [...prev, choice.tag]);
    }

    if (choice.next === "question") {
      const questionChoiceKey = getQuestionChoiceKey(world, node, choice.text);
      if (usedQuestionChoiceKeys.includes(questionChoiceKey)) {
        setMessage(t("checkpoint_complete"));
        return;
      }

      const nextQuestion = getQuestionForWorld(world, difficulty);
      if (nextQuestion) {
        const nextStoryNode = getPostQuestionNode(world, node);
        setUsedQuestionChoiceKeys((prev) =>
          prev.includes(questionChoiceKey) ? prev : [...prev, questionChoiceKey]
        );
        setPendingStoryNode(nextStoryNode);
        setQuestion(nextQuestion);
        setPendingBossQueue([]);
        setPage("question");
      } else {
        setMessage(t("no_question_found"));
      }
    } else if (choice.next === "boss") {
      const bossSet = bossQuestions[world];
      const bossList = Array.isArray(bossSet) ? bossSet : [bossSet];
      const validBossList = bossList.filter(Boolean);

      if (validBossList.length > 0) {
        setQuestion(validBossList[0]);
        setPendingBossQueue(validBossList.slice(1));
        setLastQuestionType("boss");
        setPendingStoryNode(null);
        setPage("question");
      } else {
        setMessage(t("no_question_found"));
      }
    } else {
      setPendingStoryNode(null);
      setNode(choice.next);
    }
  }

  function answer(option) {
    if (!question) {
      setMessage(t("question_lost"));
      setPage(world && worlds[world] ? "story" : "kid");
      return;
    }

    setQuestionCount((prev) => prev + 1);

    const wasBossQuestion = lastQuestionType === "boss";
    const correct = option === question.answer;

    if (correct) {
      setStars((prev) => prev + 1);
      setScore((prev) => {
        const nextScore = prev + 1;
        adjustDifficulty(nextScore);
        return nextScore;
      });
      setCorrectCount((prev) => prev + 1);
      setMessage(pickInline("Correct! ⭐ +1 star", "Correct ! ⭐ +1 étoile"));
    } else {
      setScore((prev) => {
        const nextScore = prev - 1;
        adjustDifficulty(nextScore);
        return nextScore;
      });
      setMessage(`${pickInline("Not quite.", "Pas tout à fait.")} ${localizeText(question.explain)}`);
    }

    if (pendingBossQueue.length > 0) {
      const [nextBoss, ...restBoss] = pendingBossQueue;
      setPendingBossQueue(restBoss);
      setQuestion(nextBoss);
      setLastQuestionType("boss");
      return;
    }

    if (!wasBossQuestion) {
      const canUsePendingStoryNode =
        pendingStoryNode &&
        world &&
        worlds[world] &&
        Object.prototype.hasOwnProperty.call(worlds[world], pendingStoryNode);

      if (canUsePendingStoryNode) setNode(pendingStoryNode);
      setQuestion(null);
      setPendingStoryNode(null);
      setLastAnswerCorrect(correct);
      setLastResultKind("normal");
      setPage("story");
      return;
    }

    setQuestion(null);
    setPendingStoryNode(null);
    setLastAnswerCorrect(correct);
    setLastResultKind("boss");
    setPage("result");
  }

  function saveSessionToHistory(currentWorld) {
    const counts = {};
    decisionLog.forEach((tag) => { counts[tag] = (counts[tag] || 0) + 1; });

    const accuracyRate = questionCount > 0
      ? Math.round((correctCount / questionCount) * 100)
      : 0;

    const session = {
      id: Date.now(),
      world: currentWorld,
      stars,
      questionCount,
      correctCount,
      accuracyRate,
      exploration: counts.exploration || 0,
      analysis: counts.analysis || 0,
      risk: counts.risk || 0,
      planning: counts.planning || 0,
      curiosity: counts.curiosity || 0,
      care: counts.care || 0,
      logic: counts.logic || 0,
      cooperation: counts.cooperation || 0,
      observation: counts.observation || 0,
      efficiency: counts.efficiency || 0,
      creativity: counts.creativity || 0,
      createdAt: new Date().toLocaleString()
    };

    setHistory((prev) => [session, ...prev].slice(0, MAX_HISTORY_ENTRIES));
  }

  function finishLevel() {
    if (!world) {
      setPage("kid");
      return;
    }

    unlockNextWorld(world);

    const wasCompleted = completed.includes(world);
    const nextCompleted = wasCompleted ? completed : [...completed, world];
    const justUnlockedAllWorlds =
      nextCompleted.length === worldOrder.length && completed.length < worldOrder.length;

    if (!wasCompleted) {
      setCompleted((prev) => [...prev, world]);
    }

    setShowFinalEndingAfterSummary(justUnlockedAllWorlds);
    saveSessionToHistory(world);
    setLevelState(1);
    setNode("start");
    setQuestion(null);
    setPendingBossQueue([]);
    setLastQuestionType(null);
    setAskedQuestionKeys([]);
    setUsedQuestionChoiceKeys([]);
    setPendingStoryNode(null);
    setPage("summary");
  }

  // ── History management ────────────────────────────────────────────────────

  function handleArchiveAndClear() {
    // downloadReportTxt() is kept in App.js (it uses report rendering context).
    // This hook just clears the data; App.js calls downloadReportTxt() first.
    clearHistory();
    setHistory([]);
    setShowClearConfirm(false);
    setMessage(t("report_downloaded_deleted"));
  }

  function handleClearWithoutArchive() {
    clearHistory();
    setHistory([]);
    setShowClearConfirm(false);
    setMessage(t("history_deleted"));
  }

  // ── Feedback ──────────────────────────────────────────────────────────────

  function handleSubmitFeedback() {
    const data = {
      feedbackChoice,
      feedbackNote,
      stars,
      world,
      lang,
      questionCount,
      correctCount,
      createdAt: new Date().toISOString()
    };

    try {
      const existing = readJsonFromStorage(STORAGE_KEY_FEEDBACK, []);
      localStorage.setItem(
        STORAGE_KEY_FEEDBACK,
        JSON.stringify(Array.isArray(existing) ? [data, ...existing].slice(0, 20) : [data])
      );
    } catch {
      // Ignore.
    }

    setFeedbackChoice("");
    setFeedbackNote("");
    setPage("parent");
  }

  // ── Derived values ────────────────────────────────────────────────────────

  const activeWorld = world ? worlds[world] : null;
  const activeNode = activeWorld ? activeWorld[node] : null;

  return {
    // State
    world, setWorld,
    node, setNode,
    completed,
    unlockedWorlds,
    question,
    setQuestion,
    message, setMessage,
    stars,
    setStars,
    score,
    setScore,
    difficulty,
    setDifficulty,
    decisionLog,
    questionCount,
    correctCount,
    sessionStart,
    history,
    lastQuestionType,
    setLastQuestionType,
    askedQuestionKeys,
    setAskedQuestionKeys,
    feedbackChoice, setFeedbackChoice,
    feedbackNote, setFeedbackNote,
    lastAnswerCorrect,
    setLastAnswerCorrect,
    lastResultKind,
    setLastResultKind,
    showClearConfirm, setShowClearConfirm,
    showFinalEndingAfterSummary,
    setShowFinalEndingAfterSummary,
    levelState, setLevelState,
    pendingBossQueue,
    setPendingBossQueue,
    usedQuestionChoiceKeys,
    setUsedQuestionChoiceKeys,
    pendingStoryNode,
    setPendingStoryNode,

    // Derived
    activeWorld,
    activeNode,

    // Handlers
    choose,
    answer,
    finishLevel,
    handleArchiveAndClear,
    handleClearWithoutArchive,
    handleSubmitFeedback
  };
}
